import { useCallback, useState } from "react";
import { useAbortController } from "shared/useAbortController";
import {
  fetchJson,
  FetchJsonError,
  FetchJsonReturnType,
  FetchRequestConfig,
} from "../fetchJson";

interface UseFetchJsonReturnType<T> extends Partial<FetchJsonReturnType<T>> {
  run: (
    request: RequestInfo,
    config?: FetchRequestConfig
  ) => Promise<FetchJsonReturnType<T>>;
  isRunning: boolean;
  isError: boolean;
  isSuccess: boolean;
  isCompleted: boolean;
}

export function useFetchJson<T>(): UseFetchJsonReturnType<T> {
  const abortController = useAbortController();
  const [fetchJsonResponse, setFetchJsonResponse] = useState<
    Partial<FetchJsonReturnType<T>>
  >({
    error: undefined,
    response: undefined,
    status: "not-started",
    aborted: false,
  });
  const [isRunning, setIsRunning] = useState(false);

  const run = useCallback(
    async (
      request: RequestInfo,
      config: FetchRequestConfig = {}
    ): Promise<FetchJsonReturnType<T>> => {
      try {
        const newConfig: FetchRequestConfig = {
          signal: abortController.signal,
          ...config,
        };
        setIsRunning(true);
        const result: FetchJsonReturnType<T> = await fetchJson(
          request,
          newConfig
        );
        setIsRunning(false);
        setFetchJsonResponse(result);
        return Promise.resolve(result);
      } catch (error: any) {
        if (error?.aborted) {
          // We don't want to set any state, component is unmounted
          throw error;
        }
        const { result, errorInstance } = getResult<T>(error);
        setFetchJsonResponse(result);
        setIsRunning(false);
        throw errorInstance;
      }
    },
    [abortController.signal]
  );
  const isSuccess = fetchJsonResponse.status === "success";
  const isError = fetchJsonResponse.status === "error";
  const isCompleted =
    fetchJsonResponse.status === "success" ||
    fetchJsonResponse.status === "error";

  return {
    run,
    ...fetchJsonResponse,
    isError,
    isRunning,
    isSuccess,
    isCompleted,
  };
}

type GetResultReturnType<T> = {
  result: Partial<FetchJsonReturnType<T>>;
  errorInstance: FetchJsonError;
};

function getResult<T>(error: any): GetResultReturnType<T> {
  let result: Partial<FetchJsonReturnType<T>>;
  let fetchJsonError: FetchJsonError;
  if (error instanceof FetchJsonError) {
    //@ts-ignore TS complaining about name and message property, no harm if these props exist
    result = { ...error };
    fetchJsonError = error;
  } else {
    result = {
      response: undefined,
      error: error,
      status: "error",
      aborted: false,
    };
    fetchJsonError = new FetchJsonError({ ...result });
  }

  return { result, errorInstance: fetchJsonError };
}
